import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getUrl, getProperties } from 'aws-amplify/storage'
import SparepartsDetailsModal from './SparepartsDetailsModal'
import ViewSparePartImageModal from './ViewSparePartImageModal'
import RequestReviewModal from './RequestReviewModal'
import RequestMissingItemModal from './RequestMissingItemModal'
import SearchWithFilter from '../common/SearchWithFilter'
import Dropdown from '../common/Dropdown'
import Breadcrumb from '../common/Breadcrumb'
import LoadingSpinner from '../common/LoadingSpinner'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import DataPlaceholder from '../common/DataPlaceholder'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import ExpandableText from '../common/ExpandableText'
import { toast } from '../common/Toast'
import { useWindowSize } from '../../libs/hooks'
import { ExpandMoreIcon, ExpandLessIcon } from '../../icons'
import { getImageByModel } from '../../utils/functions'
import { getDeviceSpareParts, getDeviceSparePartsTotalCount, getDeviceSparePartsModels } from '../../utils/requests/spareparts'
import { no_image_200x200 } from '../../images'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import { keyboardEvents } from '../../utils/keyboardEvents'
import './spareparts-modern.scss'

const SparepartsModern = () => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [productTypes, setProductTypes] = useState([])
  const [selectedProductType, setSelectedProductType] = useState(null)
  const [models, setModels] = useState([])
  const [selectedModel, setSelectedModel] = useState(null)
  const [spareParts, setSpareParts] = useState([])
  const [totalResults, setTotalResults] = useState(0)
  const [filterQuery, setFilterQuery] = useState(undefined)
  const [showSpinner, setShowSpinner] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [expandedSparePart, setExpandedSparePart] = useState(null)
  const [isFirstSparePartsLoad, setIsFirstSparePartsLoad] = useState(true)
  const [modelVariants, setModelVariants] = useState([])
  const [selectedModelVariant, setSelectedModelVariant] = useState(null)
  const [loadingModels, setLoadingModels] = useState(true)
  const [showRequestReviewModal, setShowRequestReviewModal] = useState(false)
  const [reviewSparePart, setReviewSparePart] = useState(null)
  const [showRequestMissingItemModal, setShowRequestMissingItemModal] = useState(false)
  const [selectedImageSparePart, setSelectedImageSparePart] = useState(null)
  const [isLoadingImageMeta, setIsLoadingImageMeta] = useState(false)

  useEffect(() => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: 'Spareparts Page: Page View',
    })

    getDeviceSparePartsModels()
      .then((response) => {
        const data = response?.data || response || []

        const grouped = {}
        data.forEach((item) => {
          if (!grouped[item.productType]) {
            grouped[item.productType] = {
              name: item.productType,
              models: [],
            }
          }
          grouped[item.productType].models.push({
            id: item.id,
            name: item.modelName,
            variants: item.variants || [],
          })
        })

        const mappedProductTypes = Object.values(grouped)
        setProductTypes(mappedProductTypes)
      })
      .catch(() => {
        setProductTypes([])
      })
      .finally(() => {
        setLoadingModels(false)
      })
  }, [])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadSpareParts(false)
    }
  }, [selectedPage])

  useEffect(() => {
    if (selectedModel) {
      let changeValueTimeout = window.setTimeout(
        () => {
          setSelectedPage(1)
          handleLoadSpareParts(true)
        },
        filterQuery ? 1000 : 0,
      )
      return () => {
        clearTimeout(changeValueTimeout)
      }
    }
  }, [filterQuery, selectedModel, selectedModelVariant])

  const handleLoadSpareParts = (withReset) => {
    let queryParams = {
      page: withReset ? 1 : selectedPage,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
      model: selectedModel.name,
    }

    if (selectedModelVariant) {
      queryParams = { ...queryParams, variantId: selectedModelVariant }
    }

    if (filterQuery) {
      queryParams = { ...queryParams, search: filterQuery }
    }
    setShowSpinner(true)
    getDeviceSpareParts(queryParams)
      .then(async (response) => {
        if (response?.data) {
          const nextSparePartsWithoutImageSrc = withReset ? response.data : [...spareParts, ...response.data]
          const nextSparePartsImagePromises = nextSparePartsWithoutImageSrc.map(async (sparePart) => {
            if (sparePart.filename && sparePart.filepath) {
              try {
                const filepath = sparePart.filepath.replace(/^\/+|\/+$/g, '')
                const filename = sparePart.filename.replace(/^\/+|\/+$/g, '')
                const key = `${filepath}/${filename}`
                const { url } = await getUrl({
                  path: key,
                })
                return {
                  ...sparePart,
                  src: url?.toString?.() || '',
                }
              } catch {
                return sparePart
              }
            }
            return sparePart
          })
          const nextSparePartsWithImg = await Promise.all(nextSparePartsImagePromises)
          setSpareParts(nextSparePartsWithImg)
          setCanLoadMore(response.nextPage)
        }
      })
      .catch(() => {
        toast.error(t('spareparts.error-loading-spareparts'))
        setCanLoadMore(false)
      })
      .finally(() => {
        setShowSpinner(false)
        setIsFirstSparePartsLoad(false)
      })
    if (withReset) {
      const getTotalCountParams = { ...queryParams }
      delete getTotalCountParams.page
      delete getTotalCountParams.limit
      getDeviceSparePartsTotalCount(getTotalCountParams)
        .then((response) => {
          setTotalResults(response.totalResults || 0)
        })
        .catch(() => {
          setTotalResults(0)
        })
    }
  }

  const handleSearchSpareParts = (value) => {
    if (value?.length > 2) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.sparepartsPage,
        label: `Spareparts Page: Search - ${value}`,
      })

      setShowSpinner(true)
      setFilterQuery(value)
    } else if (value?.length === 0 && filterQuery?.length > 0) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.sparepartsPage,
        label: 'Spareparts Page: Search Cleared',
      })

      setShowSpinner(true)
      setFilterQuery(undefined)
    }
  }

  const handleLoadMore = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: 'Spareparts Page: Load More',
    })

    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const selectProductType = (productType, toggleVisibility) => {
    if (toggleVisibility) {
      toggleVisibility(false)
    }

    if (selectedProductType === productType) {
      return
    }

    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Product Type Selected - ${productType.name}`,
    })

    setSelectedProductType(productType)

    setModels(productType.models)
    setSpareParts([])
    setIsFirstSparePartsLoad(true)
    setCanLoadMore(false)
    setSelectedModel(null)
    setFilterQuery(null)
    setExpandedSparePart(null)
    setModelVariants([])
    setSelectedModelVariant(null)
  }

  const selectModel = (modelName) => {
    const model = models.find((model) => model.name === modelName)

    if (!model) {
      return
    }

    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Model Selected - ${modelName}`,
    })

    setTotalResults(0)
    setFilterQuery(undefined)
    setExpandedSparePart(null)
    setSpareParts([])
    setIsFirstSparePartsLoad(true)
    setCanLoadMore(false)
    setSelectedModel(model)
    setSelectedModelVariant(null)
    setModelVariants(model.variants || [])
  }

  const selectModelVariant = (variantKey, toggleVisibility) => {
    if (toggleVisibility) {
      toggleVisibility(false)
    }

    if (!variantKey) {
      return
    }

    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Model Variant Selected - ${variantKey}`,
    })

    setSelectedModelVariant(variantKey)
  }

  const handleExpandComponent = (componentsLength, itemId) => {
    if (componentsLength === 0) {
      return
    }

    const isExpanding = expandedSparePart !== itemId

    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Component ${isExpanding ? 'Expanded' : 'Collapsed'} - ${itemId}`,
    })

    setExpandedSparePart(expandedSparePart === itemId ? null : itemId)
  }

  const handleOpenRequestReview = (sparePart) => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Request Review - ${sparePart.item_id}`,
    })
    setReviewSparePart(sparePart)
    setShowRequestReviewModal(true)
  }

  const handleCloseRequestReview = () => {
    setShowRequestReviewModal(false)
    setReviewSparePart(null)
  }

  const handleOpenRequestMissingItem = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: 'Spareparts Page: Request Missing Item',
    })
    setShowRequestMissingItemModal(true)
  }

  const handleCloseRequestMissingItem = () => {
    setShowRequestMissingItemModal(false)
  }

  const handleOpenImage = async (item) => {
    if (!item?.src) {
      setSelectedImageSparePart(item)
      return
    }

    setIsLoadingImageMeta(true)

    const dimensionsPromise = new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve({ width: null, height: null })
      img.src = item.src
    })

    const sizePromise = (async () => {
      if (item.filepath && item.filename) {
        try {
          const filepath = item.filepath.replace(/^\/+|\/+$/g, '')
          const filename = item.filename.replace(/^\/+|\/+$/g, '')
          const key = `${filepath}/${filename}`
          const properties = await getProperties({ path: key })
          if (properties?.size) return properties.size
        } catch (error) {
          console.error('Error loading sparepart image size via getProperties:', error)
        }
      }

      try {
        const res = await fetch(item.src, { mode: 'cors', cache: 'no-store' })
        const contentLength = res.headers.get('content-length')
        const blob = await res.blob()
        return blob.size || Number(contentLength) || null
      } catch (error) {
        console.error('Error loading sparepart image size via fetch:', error)
        return null
      }
    })()

    const [dimensions, fileSize] = await Promise.all([dimensionsPromise, sizePromise])

    setSelectedImageSparePart({
      ...item,
      fileSize,
      width: dimensions.width,
      height: dimensions.height,
    })
    setIsLoadingImageMeta(false)
  }

  return (
    <div className="spareparts-modern-wrapper d-flex flex-column">
      <Breadcrumb path={window.location.pathname} />
      <DashboardHeader headerText={t('nav.spareparts')} />
      <div className="separator" />
      {!isMobile ? (
        <div className="content">
          {productTypes?.length > 0 && (
            <>
              <div className="product-types">
                {productTypes.map((item, index) => (
                  <div
                    key={index}
                    className={'card d-flex flex-align-center' + (selectedProductType === item ? ' selected-card' : '')}
                    onClick={() => selectProductType(item)}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => e.key === keyboardEvents.ENTER && selectProductType(item)}
                  >
                    <div className="image-wrapper d-flex flex-align-center">
                      <img src={getImageByModel(item.models?.length > 0 ? item.models[0].name : '')} className="img" alt="product" />
                    </div>
                    <p className="product-type-name">{item.name}</p>
                  </div>
                ))}
              </div>

              {selectedProductType ? (
                <div className="models-wrapper">
                  <div className="dropdowns-row">
                    <div className="d-flex gap-16">
                      <div className="dropdown-group">
                        <p className="model-select-title">{t('product')}</p>
                        <Dropdown
                          className="dropdown-input"
                          placeholder={t('spareparts.no-option-selected')}
                          options={models?.map((model) => ({ key: model.name, value: model.name }))}
                          initialSelectedKey={selectedModel?.name}
                          onChange={(model) => selectModel(model.key)}
                          withFiltering
                        />
                      </div>
                      <div className="dropdown-group">
                        <p className="model-select-title">{t('model')}</p>
                        <Dropdown
                          className="dropdown-input"
                          placeholder={t('spareparts.no-option-selected')}
                          options={modelVariants.map((variant) => ({
                            key: variant.id.toString(),
                            value: variant.productModelName,
                          }))}
                          initialSelectedKey={selectedModelVariant}
                          onChange={(variant) => selectModelVariant(variant.key)}
                          isDisabled={!selectedModel}
                          withFiltering
                        />
                      </div>
                    </div>
                  </div>

                  {selectedModel && (
                    <div className="table-wrapper">
                      <div className="product-header-wrapper">
                        <div className="product-name-section">
                          <div className="image-wrapper d-flex flex-align-center">
                            <img src={getImageByModel(selectedModel.name)} className="img" alt={selectedModel.name} />
                          </div>
                          <div className="product-name-wrapper">
                            <div className="product-name">{selectedModel.name}</div>
                            <div className="product-quantity">{!showSpinner && `${totalResults} ${t('items').toLowerCase()}`}</div>
                          </div>
                        </div>
                        <div className="search-section">
                          <SearchWithFilter searchText={filterQuery} onSearch={(e) => handleSearchSpareParts(e)} />
                        </div>
                        <div className="request-missing-section">
                          <SimpleButton className="request-missing-item-button simple-button submit-button" onClick={handleOpenRequestMissingItem}>
                            {t('spareparts.request-missing-sparepart')}
                          </SimpleButton>
                        </div>
                      </div>
                      {spareParts.length > 0 && (
                        <table className="spare-parts-table">
                          <thead>
                            <tr className="table-row header-row">
                              <th className="table-column image">{t('image')}</th>
                              <th className="table-column item-id">{t('spareparts.part-no')}</th>
                              <th className="table-column description">{t('description')}</th>
                              <th className="table-column components last-column">{t('spareparts.components')}</th>
                              <th className="table-column actions">{t('actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {spareParts.map((item, index) => (
                              <Fragment key={index}>
                                <tr className={'table-row' + (expandedSparePart === item.item_id ? ' without-border' : '')}>
                                  <td className="table-column image">
                                    <img
                                      className="clickable-image"
                                      src={item.src || no_image_200x200}
                                      alt="sparepart"
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => handleOpenImage(item)}
                                      onKeyDown={(e) => e.key === keyboardEvents.ENTER && handleOpenImage(item)}
                                      onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = no_image_200x200
                                      }}
                                    />
                                  </td>
                                  <td className="table-column item-id">{item.item_id}</td>
                                  <td className="table-column description">
                                    <ExpandableText text={item.description} />
                                  </td>
                                  <td
                                    className={'table-column components last-column' + (item.components?.length > 0 ? ' is-expandable' : '')}
                                    onClick={() => handleExpandComponent(item.components?.length || 0, item.item_id)}
                                    tabIndex={0}
                                    role="button"
                                    onKeyDown={(e) => e.key === keyboardEvents.ENTER && handleExpandComponent(item.components?.length || 0, item.item_id)}
                                  >
                                    <div className="components-column-wrapper">
                                      {item.components?.length || 0}
                                      {item.components?.length > 0 && <>{expandedSparePart === item.item_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}</>}
                                    </div>
                                  </td>
                                  <td className="table-column actions">
                                    <div className="request-review-button" onClick={() => handleOpenRequestReview(item)}>
                                      {t('spareparts.submit-feedback')}
                                    </div>
                                  </td>
                                </tr>
                                {expandedSparePart === item.item_id && (
                                  <tr className="table-row">
                                    <td className="table-column expanded-components" colSpan="5">
                                      <div className="components-list-wrapper">
                                        <ul className="components-list">
                                          {item.components.map((component, index) => (
                                            <li className="component-wrapper" key={index}>
                                              <div className="component-description">{component.description}</div>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {spareParts.length === 0 && !showSpinner && !isFirstSparePartsLoad && (
                        <EmptyPageResults title={t('spareparts.no-spareparts')} subtitle={t('cannot_find_matching_search')} />
                      )}
                      {canLoadMore && (
                        <div className="buttons-wrapper">
                          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
                            {t('load_more')}
                          </SimpleButton>
                        </div>
                      )}
                    </div>
                  )}
                  {!selectedModel && <EmptyPageResults title={t('spareparts.no-model-selected')} subtitle={t('spareparts.select-model')} />}
                </div>
              ) : (
                <DataPlaceholder titlePlaceholder={t('spareparts.no-product-type-selected')} subtitlePlaceholder={t('spareparts.select-product-type')} />
              )}
            </>
          )}
        </div>
      ) : (
        <div className="content">
          {productTypes.map((item, index) => (
            <div key={index} className="card d-flex flex-align-center" onClick={() => selectProductType(item)}>
              <div className="image-wrapper d-flex flex-align-center">
                <img src={getImageByModel(item.models?.length > 0 ? item.models[0].name : '')} className="img" alt="product" />
              </div>
              <p className="product-type-name">{item.name}</p>
            </div>
          ))}
        </div>
      )}
      {isMobile && selectedProductType && (
        <SparepartsDetailsModal
          selectedProductType={selectedProductType}
          selectedModel={selectedModel}
          onSelectModel={(modelName) => selectModel(modelName)}
          modelVariants={modelVariants}
          selectedModelVariant={selectedModelVariant}
          onSelectModelVariant={(variantKey) => selectModelVariant(variantKey)}
          spareParts={spareParts}
          isFirstSparePartsLoad={isFirstSparePartsLoad}
          totalResults={totalResults}
          filterQuery={filterQuery}
          onSearchSpareParts={(e) => handleSearchSpareParts(e)}
          expandedSparePart={expandedSparePart}
          onExpandSparePart={(componentsLength, itemId) => handleExpandComponent(componentsLength, itemId)}
          canLoadMore={canLoadMore}
          onLoadMore={() => handleLoadMore()}
          showSpinner={showSpinner}
          onRequestReview={(item) => handleOpenRequestReview(item)}
          onRequestMissingItem={handleOpenRequestMissingItem}
          onOpenImage={(item) => handleOpenImage(item)}
          closeModal={() => setSelectedProductType(null)}
        />
      )}
      {(showSpinner || loadingModels || isLoadingImageMeta) && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
      {showRequestReviewModal && reviewSparePart && <RequestReviewModal sparePart={reviewSparePart} onClose={handleCloseRequestReview} />}
      {showRequestMissingItemModal && <RequestMissingItemModal selectedModel={selectedModel} onClose={handleCloseRequestMissingItem} />}
      {selectedImageSparePart && <ViewSparePartImageModal sparePart={selectedImageSparePart} closeModal={() => setSelectedImageSparePart(null)} />}
    </div>
  )
}

export default SparepartsModern

